/**
 * API Client
 * Centralized API request handling with error handling and authentication
 */

class APIError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Base fetch wrapper with error handling
 */
async function apiFetch(url, options = {}) {
  const defaultOptions = {
    credentials: 'include', // Include cookies for session auth
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  // headers last, so a caller's own headers can't drop the JSON Content-Type:
  // the server rejects non-JSON state-changing requests (apps/middleware/csrfGuard.js).
  const config = { ...defaultOptions, ...options, headers: defaultOptions.headers };

  try {
    const response = await fetch(url, config);

    // Handle different response types
    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    // Check for errors
    if (!response.ok) {
      throw new APIError(
        data.message || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        data
      );
    }

    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    // Network or other errors
    throw new APIError(
      error.message || 'Network error occurred',
      0,
      null
    );
  }
}

function appendSearchParams(target, filters = {}) {
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== '') {
          target.append(key, item);
        }
      });
    } else {
      target.append(key, value);
    }
  });

  return target;
}

/**
 * API Client methods
 */
const api = {
  // Authentication
  auth: {
    getSession: () => apiFetch('/api/auth/session'),
    signIn: (credentials) => apiFetch('/auth/signin', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),
    signOut: () => apiFetch('/api/auth/signout', {
      method: 'POST',
    }),
    stopImpersonation: () => apiFetch('/api/admin/v1/impersonation/stop', {
      method: 'POST',
    }),
    requestPasswordReset: ({ email }) => apiFetch('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
    checkResetToken: ({ token, hash }) => apiFetch('/api/auth/check-reset-token', {
      method: 'POST',
      body: JSON.stringify({ token, hash }),
    }),
    resetPassword: ({ token, hash, password }) => apiFetch('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, hash, password }),
    }),
  },

  // Dashboard
  dashboard: {
    /**
     * Fetch dashboard context (user, permissions, navigation)
     * @param {Object} [options]
     * @param {string|number} [options.countyId] - Optional county override
     * @param {Object} [options.filters] - Additional filter params to pass through
     * @returns {Promise<Object>}
     */
    getContext({ countyId, filters = {} } = {}) {
      const params = appendSearchParams(new URLSearchParams(), filters);
      if (countyId) {
        params.set('countyId', countyId);
      }
      const query = params.toString();
      const url = query ? `/api/dashboard/context?${query}` : '/api/dashboard/context';
      return apiFetch(url);
    },

    /**
     * Legacy alias for getContext that accepts positional arguments
     * @deprecated Use getContext({ countyId, filters }) instead.
     */
    getData(countyId, filters = {}) {
      return this.getContext({ countyId, filters });
    },

    getCards(countyId, params = {}) {
      const searchParams = appendSearchParams(new URLSearchParams(), params);
      const query = searchParams.toString();
      const base = `/api/dashboard/${countyId}/cards`;
      return apiFetch(query ? `${base}?${query}` : base);
    },

    /**
     * Fetch a single dashboard card response for the provided identifiers.
     * @param {number|string} countyId
     * @param {string} pageId
     * @param {string} cardId
     * @param {Object} [filters={}]
     * @returns {Promise<Object>}
     */
    getCard(countyId, pageId, cardId, filters = {}) {
      if (countyId === undefined || countyId === null) {
        throw new Error('countyId is required when calling api.dashboard.getCard');
      }
      if (!pageId) {
        throw new Error('pageId is required when calling api.dashboard.getCard');
      }
      if (!cardId) {
        throw new Error('cardId is required when calling api.dashboard.getCard');
      }

      const params = appendSearchParams(new URLSearchParams(), filters);
      const base = `/api/dashboard/${countyId}/${pageId}/sql/${cardId}`;
      const query = params.toString();
      return apiFetch(query ? `${base}?${query}` : base);
    },

    /**
     * Fetch multiple dashboard cards in a single request.
     * @param {number|string} countyId
     * @param {string} pageId
     * @param {Array<string>} cardIds
     * @param {Object} [filters={}]
     * @returns {Promise<Object>}
     */
    getPageCards(countyId, pageId, cardIds = [], filters = {}) {
      if (countyId === undefined || countyId === null) {
        throw new Error('countyId is required when calling api.dashboard.getPageCards');
      }
      if (!pageId) {
        throw new Error('pageId is required when calling api.dashboard.getPageCards');
      }

      const params = appendSearchParams(new URLSearchParams(), filters);
      if (Array.isArray(cardIds) && cardIds.length > 0) {
        params.set('cardIds', cardIds.join(','));
      }

      const base = `/api/dashboard/${countyId}/${pageId}/cards`;
      const query = params.toString();
      return apiFetch(query ? `${base}?${query}` : base);
    },
  },

  // Predictive Analytics
  predictive: {
    /**
     * Fetch predictive context for SPA shells
     * @param {Object} [options]
     * @param {string|number} [options.countyId]
     * @param {Object} [options.filters]
     * @returns {Promise<Object>}
     */
    getContext({ countyId, filters = {} } = {}) {
      const params = new URLSearchParams(filters);
      if (countyId) {
        params.set('countyId', countyId);
      }
      const query = params.toString();
      const url = query ? `/api/predictive/context?${query}` : '/api/predictive/context';
      return apiFetch(url);
    },

    /**
     * Fetch cohort data for provided query identifier.
     * Defaults to the legacy `cohort_details` query.
     * @param {string} [queryId='cohort_details']
     * @param {Object} [params]
     * @returns {Promise<Object>}
     */
    getCohort(queryId = 'cohort_details', params = {}) {
      const query = new URLSearchParams(params);
      const search = query.toString();
      const base = `/api/predictive/cohort/sql/${encodeURIComponent(queryId)}`;
      const url = search ? `${base}?${search}` : base;
      return apiFetch(url);
    },

    /**
     * Legacy alias for getCohort('cohort_details', params).
     * @deprecated Use getCohort instead.
     */
    getCohorts(params = {}) {
      return this.getCohort('cohort_details', params);
    },

    getFilters: () => apiFetch('/api/predictive/filters'),
  },

  // Validation
  validate: {
    /**
     * Fetch validation module context and user metadata
     * @param {Object} [options]
     * @param {string|number} [options.countyId]
     * @returns {Promise<Object>}
     */
    getContext({ countyId } = {}) {
      const params = new URLSearchParams();
      if (countyId) {
        params.set('countyId', countyId);
      }
      const query = params.toString();
      const url = query ? `/api/validate/context?${query}` : '/api/validate/context';
      return apiFetch(url);
    },

    /**
     * Log a validation event (success or failure)
     * @param {Object} event
     * @returns {Promise<Object>}
     */
    logEvent(event) {
      return apiFetch('/api/validate/event', {
        method: 'POST',
        body: JSON.stringify(event),
      });
    },

    /**
     * Fetch aggregated validation activity
     * @param {Object} [filters] - Optional filters: providerType, partnerId, month, success
     * @returns {Promise<Object>}
     */
    getActivity(filters = {}) {
      const params = appendSearchParams(new URLSearchParams(), filters);
      const query = params.toString();
      const url = query ? `/api/validate/activity?${query}` : '/api/validate/activity';
      return apiFetch(url);
    },
  },

  // Submissions coverage grid (Probation-only; the server returns 403 otherwise)
  submissions: {
    /**
     * Fetch the agency x month submission coverage grid.
     * @param {Object} [range] - Optional 'YYYY-MM' bounds; both default server-side
     * @param {string} [range.start]
     * @param {string} [range.end]
     * @returns {Promise<{months: string[], rows: Object[]}>}
     */
    getCoverage({ start, end } = {}) {
      const params = appendSearchParams(new URLSearchParams(), { start, end });
      const query = params.toString();
      const url = query ? `/api/submissions/coverage?${query}` : '/api/submissions/coverage';
      return apiFetch(url);
    },
  },

  // Settings
  settings: {
    getAccount: () => apiFetch('/api/settings/account'),
    updateAccount: (payload) => apiFetch('/api/settings/account', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
    updatePassword: (passwords) => apiFetch('/api/settings/password', {
      method: 'PATCH',
      body: JSON.stringify(passwords),
    }),
  },
};

export { api, APIError };
export default api;
